import React, { useState, useEffect } from 'react';

const AdminNewsletter = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadSubscribers = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/newsletter`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setSubscribers(data);
        } catch (error) {
            console.error('Error loading subscribers:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadSubscribers();
    }, [loadSubscribers]);

    const deleteSubscriber = async (id) => {
        if (!window.confirm('Are you sure you want to remove this subscriber?')) return;

        try {
            const response = await fetch(`${API_URL}/api/newsletter/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                loadSubscribers();
                alert('Subscriber removed!');
            }
        } catch (error) {
            console.error('Error deleting subscriber:', error);
            alert('Error deleting subscriber');
        }
    };

    const exportSubscribers = () => {
        const emails = subscribers.filter(s => s.isActive).map(s => s.email).join(',');
        const blob = new Blob([emails], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'newsletter-subscribers.txt';
        a.click();
    };

    const filteredSubscribers = subscribers.filter(sub => {
        if (filter === 'all') return true;
        return filter === 'active' ? sub.isActive : !sub.isActive;
    });

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Newsletter Subscribers</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={exportSubscribers}
                        className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
                    >
                        <i className="fas fa-download mr-2"></i>Export Emails
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        All ({subscribers.length})
                    </button>
                    <button
                        onClick={() => setFilter('active')}
                        className={`px-4 py-2 rounded ${filter === 'active' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
                    >
                        Active ({subscribers.filter(s => s.isActive).length})
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscribed Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSubscribers.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                    No subscribers found
                                </td>
                            </tr>
                        ) : (
                            filteredSubscribers.map(subscriber => (
                                <tr key={subscriber.id}>
                                    <td className="px-6 py-4 font-semibold">{subscriber.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {new Date(subscriber.subscribedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded ${subscriber.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {subscriber.isActive ? 'Active' : 'Unsubscribed'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => deleteSubscriber(subscriber.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Remove"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">
                    <i className="fas fa-info-circle mr-2"></i>Newsletter Statistics
                </h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                        <span className="text-gray-600">Total Subscribers:</span>
                        <span className="ml-2 font-bold">{subscribers.length}</span>
                    </div>
                    <div>
                        <span className="text-gray-600">Active:</span>
                        <span className="ml-2 font-bold text-green-600">
                            {subscribers.filter(s => s.isActive).length}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-600">Unsubscribed:</span>
                        <span className="ml-2 font-bold text-gray-600">
                            {subscribers.filter(s => !s.isActive).length}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminNewsletter;
